import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API_URL } from "../../constants";
import {
  Box,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Typography,
  Grid,
  useTheme,
  Tooltip,
  IconButton,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { keyframes } from "@emotion/react";
import { Info, Check, CircleArrowRight, Clock, CreditCard } from "lucide-react";
import CardMarker from "../../components/CockpitCards/CardMarker";
import { useTranslation } from "../../language";
import { useNavigate } from "react-router-dom";
import CustomDialogSmall from "../../components/Dialog/DiaCompSmall";
import ButtonsMainGoTo from "../../components/Button/ButtonMainGoTo";
import LabWorkflowSteps from "../../components/Steps/LabWorkflowSteps";

const EASE_OUT = "cubic-bezier(0.23, 1, 0.32, 1)";

// Detail panel re-enters on every visit switch so the change reads as a
// state update rather than an instant content swap.
const detailEnter = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const detailFade = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`;

// States that have an actionable button in the table
const STATE_ROUTES = {
  1: "/StateLabAppointment",
  2: "/StateAddOn",
  3: "/StateQuest",
  4: "/LabvisitStepAdmin",
  41: "/MicrobiomePath",
  42: "/MicrobiomePath",
};

export default function AccountLabVisits() {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const { t } = useTranslation();
  const navigate = useNavigate();

  // ── Subscription ───────────────────────────────────────────────────────────
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [subLoading, setSubLoading] = useState(true);
  const [subError, setSubError] = useState(null);

  // ── Lab visits ─────────────────────────────────────────────────────────────
  const [labVisits, setLabVisits] = useState([]);
  const [visitsLoading, setVisitsLoading] = useState(true);
  const [visitsError, setVisitsError] = useState(null);

  // ── UI ─────────────────────────────────────────────────────────────────────
  const [openInfo, setOpenInfo] = useState(false);
  const [openSub, setOpenSub] = useState(false);

  // ── Fetch subscription ─────────────────────────────────────────────────────
  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/customer-profile/me/`);
        setSubscriptionStatus(data);
      } catch (err) {
        setSubError(
          err.response?.data?.detail ||
            err.response?.data?.message ||
            err.message ||
            t("acc_visit_err_sub")
        );
      } finally {
        setSubLoading(false);
      }
    };

    fetchSubscription();
  }, []);

  // ── Fetch lab visits ───────────────────────────────────────────────────────
  useEffect(() => {
    let mounted = true;

    const fetchLabVisits = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/user-lab-visits/`);
        if (mounted) {
          setLabVisits(Array.isArray(data) ? data : data.items || []);
        }
      } catch (err) {
        if (mounted) setVisitsError(t("acc_visit_err_visits"));
      } finally {
        if (mounted) setVisitsLoading(false);
      }
    };

    fetchLabVisits();
    return () => { mounted = false; };
  }, []);

  // ── Derive the single actionable state=1 visit that gets a live button.
  // Among all state=1 visits, the one with the LOWEST visit_number is the
  // earliest unstarted visit — that is the one the user should action next.
  // Fall back to lowest id if visit_number is absent.
  const mostRecentState1Id = useMemo(() => {
    const state1Visits = labVisits.filter((v) => v.state === 1);
    if (!state1Visits.length) return null;

    return state1Visits.reduce((best, v) => {
      const bestScore = best.visit_number ?? best.id;
      const vScore = v.visit_number ?? v.id;
      return vScore < bestScore ? v : best;
    }).id;
  }, [labVisits]);

  // ── Sorted visits (ascending by visit_number) ──────────────────────────────
  const sortedLabVisits = useMemo(
    () => [...labVisits].sort((a, b) => (a.visit_number ?? a.id) - (b.visit_number ?? b.id)),
    [labVisits]
  );

  // ── Next visit number (one after the last completed visit) ─────────────────
  const nextVisitNumber = useMemo(() => {
    const completed = labVisits.filter((v) => v.state === 6);
    if (!completed.length) {
      const nums = labVisits.map((v) => v.visit_number ?? v.id);
      return nums.length ? Math.min(...nums) : null;
    }
    return Math.max(...completed.map((v) => v.visit_number ?? 0)) + 1;
  }, [labVisits]);

  // ── Mobile (xs) visit selector — the table doesn't fit narrow screens, so
  // xs shows a row of visit-number circles instead; picking one reveals its
  // details stacked vertically. Stays in sync with however many visits the
  // account actually has (e.g. after adding another subscription).
  const [selectedVisitId, setSelectedVisitId] = useState(null);
  useEffect(() => {
    if (!sortedLabVisits.length) return;
    if (!selectedVisitId || !sortedLabVisits.some((v) => v.id === selectedVisitId)) {
      const nextVisit = sortedLabVisits.find((v) => (v.visit_number ?? v.id) === nextVisitNumber);
      setSelectedVisitId((nextVisit ?? sortedLabVisits[0]).id);
    }
  }, [sortedLabVisits, selectedVisitId, nextVisitNumber]);
  const selectedVisit = sortedLabVisits.find((v) => v.id === selectedVisitId) || null;

  // ── Table columns ──────────────────────────────────────────────────────────
  const columns = useMemo(
    () => [
      {
        accessorKey: "visit_number",
        header: t("acc_visit28"),
        cell: (info) => (
          <Chip
            label={info.getValue()}
            size="small"
            variant="outlined"
            sx={{
              bgcolor: theme.palette.mode === "light" ? "grey.C200" : "greydark.C800",
              borderColor: theme.palette.mode === "light" ? "grey.C400" : "greydark.C400",
              fontWeight: 400,
            }}
          />
        ),
      },
      {
        accessorKey: "visit_date",
        header: t("acc_visit15"),
        cell: (info) =>
          info.getValue()
            ? new Date(info.getValue()).toLocaleDateString()
            : t("visitplan_14"),
      },
      {
        accessorKey: "laboratory",
        header: t("acc_visit16"),
        cell: (info) => info.getValue()?.name || t("visitplan_14"),
      },
      {
        accessorKey: "addon_names",
        header: t("acc_visit17"),
        cell: (info) => {
          const addons = info.getValue();
          return addons?.length > 0 ? addons.join(", ") : t("visitplan_14");
        },
      },
      {
        accessorKey: "state",
        header: t("acc_visit22"),
        // TanStack Table passes the full row via info.row.original
        cell: (info) => {
          const status = info.getValue();
          const visitId = info.row.original.id;
          const visitNumber = info.row.original.visit_number;
          const route = STATE_ROUTES[status];

          const icon =
            status === 6 ? (
              <Check size={18} color={theme.palette.traffic.green} />
            ) : status === 5 ? (
              <Clock size={18} />
            ) : visitNumber === nextVisitNumber ? (
              <CircleArrowRight size={18} />
            ) : (
              <Clock size={18} />
            );

          if (!route) {
            return (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {icon}
                {status === 6 ? t("acc_visit27") : status === 5 ? t("acc_visit26") : ""}
              </Box>
            );
          }

          // Only the earliest unbooked visit can be booked now; later ones are
          // shown as pending instead of a faded, near-invisible disabled button.
          if (status === 1 && visitId !== mostRecentState1Id) {
            return (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                {icon}
                {t("acc_visit_pending")}
              </Box>
            );
          }

          return (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              {icon}
              <ButtonsMainGoTo
                name={
                  status === 1 ? t("visitplan_11") :
                  status === 2 ? t("acc_visit24") :
                  status === 3 ? t("acc_visit25") :
                                 t("acc_visit24")
                }
                to={route}
                state={{ labVisitId: visitId }}
                size="small"
                sx={{
                  minWidth: "unset",
                  py: 0.5,
                  px: 1.5,
                  fontSize: "0.7rem",
                  lineHeight: 1.4,
                }}
              />
            </Box>
          );
        },
      },
    ],
    [t, mostRecentState1Id, nextVisitNumber, theme]
  );

  // ── Subscription loading / error guards ────────────────────────────────────
  if (subLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (subError) {
    return <Alert severity="error" sx={{ mt: 2 }}>{subError}</Alert>;
  }

  if (!subscriptionStatus) {
    return (
      <Typography mt={2} color="text.secondary">
        {t("acc_visit_no_sub")}
      </Typography>
    );
  }

  // ── Visits loading / error guards ──────────────────────────────────────────
  if (visitsLoading) {
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (visitsError) {
    return <Alert severity="error" sx={{ mt: 2 }}>{visitsError}</Alert>;
  }

  if (!labVisits.length) {
    return (
      <Typography mt={2} color="text.secondary">
        {t("acc_visit00")}
      </Typography>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <Grid
      container
      spacing={1}
      sx={{
        maxWidth: 1000,
        mb: 1,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
    >
      {/* ── Lab visits card ── */}
      <Grid
        size={12}
        sx={{ display: "flex", justifyContent: "center", alignItems: "center" }}
      >
        <CardMarker
          glass
          headline={
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1, width: "100%" }}>
              {t("acc_visit13")}
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, flexShrink: 0 }}>
              <Tooltip title="Info" placement="top">
                <IconButton
                  size="small"
                  onClick={() => setOpenInfo(true)}
                  sx={{ "&:hover": { transform: "scale(1.2)" } }}
                >
                  <Info size={18} />
                </IconButton>
              </Tooltip>
              <Tooltip title={t("acc_visit_sub_title")} placement="top">
                <IconButton
                  size="small"
                  onClick={() => setOpenSub(true)}
                  sx={{ "&:hover": { transform: "scale(1.2)" } }}
                >
                  <CreditCard size={18} />
                </IconButton>
              </Tooltip>
              </Box>
            </Box>
          }
          cardlines={
            <>
              {/* Visits — circle selector + stacked detail view, now used at
                  every breakpoint instead of just falling back to a table on
                  wider screens */}
              <Box sx={{ mt: 2 }}>
                <Typography
                  variant="caption"
                  fontWeight={700}
                  sx={{ textTransform: "uppercase", letterSpacing: 0.4, color: "text.secondary", display: "block", mb: 1 }}
                >
                  {t("acc_visit28")}
                </Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", columnGap: 2, rowGap: 1.5, mb: 2.5 }}>
                  {sortedLabVisits.map((v) => {
                    const selected = v.id === selectedVisitId;
                    const completed = v.state === 6;
                    return (
                      <Box key={v.id} sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 0.5 }}>
                      <Box
                        onClick={() => setSelectedVisitId(v.id)}
                        role="button"
                        tabIndex={0}
                        aria-pressed={selected}
                        aria-label={`${t("acc_visit28")} ${v.visit_number ?? v.id}${completed ? ` – ${t("acc_visit27")}` : ""}`}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            setSelectedVisitId(v.id);
                          }
                        }}
                        sx={{
                          width: 36,
                          height: 36,
                          borderRadius: "50%",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          cursor: "pointer",
                          fontWeight: 600,
                          fontSize: "0.875rem",
                          border: "1px solid",
                          borderColor: selected
                            ? theme.palette.primary.main
                            : (theme.palette.mode === "light" ? "grey.C400" : "greydark.C400"),
                          bgcolor: selected
                            ? theme.palette.primary.main
                            : (theme.palette.mode === "light" ? "grey.C200" : "greydark.C800"),
                          color: selected ? "#fff" : "text.primary",
                          userSelect: "none",
                          transition: `background-color 150ms ease, border-color 150ms ease, transform 160ms ${EASE_OUT}`,
                          "@media (hover: hover) and (pointer: fine)": {
                            "&:hover": !selected ? { borderColor: theme.palette.primary.main } : {},
                          },
                          "&:active": { transform: "scale(0.94)" },
                          "&:focus-visible": {
                            outline: `2px solid ${theme.palette.primary.main}`,
                            outlineOffset: 2,
                          },
                          "@media (prefers-reduced-motion: reduce)": {
                            "&:active": { transform: "none" },
                          },
                        }}
                      >
                        {v.visit_number ?? v.id}
                      </Box>
                      {completed && (
                        <Check size={16} strokeWidth={3} color={theme.palette.traffic.green} aria-hidden />
                      )}
                      </Box>
                    );
                  })}
                </Box>

                {selectedVisit && (
                  <Box
                    key={selectedVisit.id}
                    sx={{
                      position: "relative",
                      display: "flex", flexDirection: "column", gap: 2, p: 2, borderRadius: 3, border: "1px solid", borderColor: "divider",
                      animation: `${detailEnter} 200ms ${EASE_OUT} both`,
                      "@media (prefers-reduced-motion: reduce)": {
                        animation: `${detailFade} 150ms ease both`,
                      },
                    }}
                  >
                    <Chip
                      label={selectedVisit.state === 6 ? t("acc_visit_completed") : t("acc_visit_pending")}
                      size="small"
                      sx={{
                        position: "absolute", top: 16, right: 16,
                        bgcolor: selectedVisit.state === 6 ? theme.palette.traffic.green : theme.palette.traffic.orange,
                        color: "#fff",
                        fontWeight: 700, fontSize: "0.65rem", height: 22, borderRadius: "6px",
                        textTransform: "uppercase",
                        "& .MuiChip-label": { px: 1 },
                      }}
                    />
                    {columns
                      .filter((col) => col.accessorKey !== "visit_number")
                      .map((col) => {
                        const value = selectedVisit[col.accessorKey];
                        const info = { getValue: () => value, row: { original: selectedVisit } };
                        return (
                          <Box key={col.accessorKey}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ textTransform: "uppercase", letterSpacing: 0.4, fontSize: "0.65rem", display: "block", mb: 0.5 }}
                            >
                              {col.header}
                            </Typography>
                            {col.cell(info)}
                          </Box>
                        );
                      })}
                  </Box>
                )}
              </Box>
            </>
          }
        />
      </Grid>

      {/* ── Info dialog ── */}
      <CustomDialogSmall
        open={openInfo}
        onClose={() => setOpenInfo(false)}
        title={t("acc_visit_steps_title")}
        content={
          <Box sx={{ mt: 2, mb: 1, textAlign: "left" }}>
            <Typography variant="subtitle1" fontWeight={500} sx={{ mb: 4 }}>
              {t("acc_visit_steps_text")}
            </Typography>
            <LabWorkflowSteps stepNumber={0} />
          </Box>
        }
      />

      {/* ── Subscription dialog ── */}
      <CustomDialogSmall
        open={openSub}
        onClose={() => setOpenSub(false)}
        title={t("acc_visit_sub_title")}
        content={
          /* Subscription summary — liquid glass shell, same recipe used
             elsewhere in the app (translucent fill + blur + inset highlight) */
          <Box sx={{
            width: "100%", mt: 2, p: 1.5, position: "relative",
            borderRadius: "20px",
            overflow: "hidden",
            textAlign: "left",
            bgcolor: isDark ? alpha("#fff", 0.04) : alpha("#fff", 0.5),
            backdropFilter: "blur(28px) saturate(180%)",
            WebkitBackdropFilter: "blur(28px) saturate(180%)",
            border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(255,255,255,0.65)"}`,
            boxShadow: isDark
              ? "inset 0 1px 0 rgba(255,255,255,0.06), 0 8px 32px rgba(0,0,0,0.55)"
              : "inset 0 1px 0 rgba(255,255,255,0.7), 0 4px 24px rgba(0,0,0,0.06)",
          }}>
            <Typography variant="caption" fontWeight={700} sx={{ textTransform: "uppercase", letterSpacing: 0.6, color: "text.secondary", display: "block", mb: 1 }}>
              {subscriptionStatus?.product_name}
            </Typography>

            <Chip
              label={subscriptionStatus?.subscription_active ? t("bt_sub_active") : t("bt_sub_inactive")}
              size="small"
              sx={{
                fontWeight: 700, fontSize: "0.7rem", mb: 1.25,
                bgcolor: subscriptionStatus?.subscription_active
                  ? theme.palette.traffic.green
                  : theme.palette.traffic.red,
                color: "#fff",
              }}
            />

            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2 }}>
              {[
                {
                  label: t("visitplan_3"),
                  value: subscriptionStatus?.current_period_start
                    ? new Date(subscriptionStatus.current_period_start).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
                    : "—",
                },
                {
                  label: t("visitplan_4"),
                  value: subscriptionStatus?.current_period_end
                    ? new Date(subscriptionStatus.current_period_end).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
                    : "—",
                },
                { label: t("visitplan_5"), value: subscriptionStatus?.days_remaining ?? "—", pushRight: true },
              ].map(({ label, value, pushRight }) =>
                pushRight ? (
                  <Box key={label} sx={{ width: "100%", display: "flex", alignItems: "center", gap: 0.75 }}>
                    <Typography variant="caption" color="text.disabled" sx={{ textTransform: "uppercase", letterSpacing: 0.4, fontSize: "0.6rem" }}>{label}</Typography>
                    <Typography variant="caption" fontWeight={600}>{value}</Typography>
                  </Box>
                ) : (
                  <Box key={label}>
                    <Typography variant="caption" color="text.disabled" sx={{ display: "block", textTransform: "uppercase", letterSpacing: 0.4, fontSize: "0.6rem" }}>{label}</Typography>
                    <Divider sx={{ my: 0.5 }} />
                    <Typography variant="subtitle2" fontWeight={400}>{value}</Typography>
                  </Box>
                )
              )}
            </Box>
          </Box>
        }
      />
    </Grid>
  );
}